import bugsnag from "@bugsnag/js";
import classNames from "classnames";
import noop from "lodash/noop";
import sumBy from "lodash/sumBy";
import React, { Dispatch, FunctionComponent, ReactNode, useCallback, useMemo } from "react";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconCheckFilled } from "../../../../icons/check-filled.svg";
import BaseUploadProvider from "../../../../providers/upload/BaseUploadProvider";
import { CustomizationAction } from "../../../../states/customization";
import { SelectionPhoto } from "../../../../types/selection/selection";
import { imageUrlFromSelectionPhoto } from "../../../../utils/selection/photo";
import Draggable from "../../../Draggable";
import "./style.scss";

interface Props {
  draggable?: boolean;
  hideUsed?: boolean;
  onSelect?: (selectionPhoto: SelectionPhoto) => void;
  pageKey?: string;
  selected?: boolean;
  selectionPhoto: SelectionPhoto;
  showCount?: boolean;
  uploadProvider?: BaseUploadProvider;
}

const SelectionProviderBrowserPhoto: FunctionComponent<Props> = ({
  draggable = false,
  hideUsed = false,
  onSelect,
  pageKey,
  selected,
  selectionPhoto,
  showCount = false,
  uploadProvider,
}) => {
  const {
    configuration,
    content,
    dispatch,
    selection,
    ui: { editedPicture },
  } = useCustomizationContext();

  const imageUrl = imageUrlFromSelectionPhoto(selectionPhoto, "thumbnail");

  const count = useMemo(
    () =>
      sumBy(content.pages, (page) =>
        sumBy(page.editablePictures, (editablePicture) => {
          if (editablePicture.selectionPhotoKey === selectionPhoto.key) {
            return 1;
          }

          const selectionItem = selection.photos.find(
            (selectionPicture) => selectionPicture.key === editablePicture.selectionPhotoKey
          );
          if (!selectionItem) {
            return 0;
          }
          return selectionItem.provider === selectionPhoto.provider &&
            selectionItem.providerRef === selectionPhoto.providerRef
            ? 1
            : 0;
        })
      ),
    [content.pages, selection, selectionPhoto]
  );

  const uploadOrNot = useCallback(() => {
    if (!uploadProvider || selectionPhoto.fotomKey) {
      return;
    }

    uploadProvider
      .upload(selectionPhoto)
      .then((updatedSelectionPhoto) => {
        dispatch({
          selectionPhoto: updatedSelectionPhoto,
          type: "addOrUpdateSelectionPhoto",
        });
      })
      .catch((e: Error) => {
        bugsnag.notify(e);
      });
  }, [dispatch, selectionPhoto, uploadProvider]);

  let onClick = noop;
  switch (content.templateType) {
    case "book":
    case "mural":
    case "print":
      onClick = () => {
        uploadOrNot();
        if (onSelect) {
          onSelect(selectionPhoto);
        }

        if (pageKey) {
          if (editedPicture) {
            dispatch({
              configuration,
              editablePictureKey: editedPicture.editablePictureKey,
              pageKey,
              selectionPhoto,
              tryToFillFirstEmptyPicture: false,
              type: "addPicture",
              undoKey: `PictureEditorView-${pageKey}`,
            });
          } else if (!onSelect) {
            dispatch({
              configuration,
              pageKey,
              selectionPhoto,
              tryToFillFirstEmptyPicture: true,
              type: "addPicture",
            });
          }
        } else if (!onSelect) {
          dispatch({
            avoidCover: true,
            configuration,
            selectionPhoto,
            tryToFillFirstEmptyPicture: true,
            type: "addPicture",
          });
        }
      };
      break;
    default: {
      const exhaustiveCheck: never = content;
      throw new Error(
        `Unsupported templateType ${(exhaustiveCheck as { templateType: string }).templateType}`
      );
    }
  }

  const isSelected = useMemo(
    () => (!showCount ? selected : count === 1),
    [onSelect, selected, count]
  );

  if (hideUsed && count > 0) {
    return null;
  }

  const renderedPhoto = (
    <div
      key={selectionPhoto.key}
      className={classNames("SelectionProviderBrowserPhoto", {
        "SelectionProviderBrowserPhoto--selected": (showCount && count > 0) || isSelected,
      })}
      onClick={onClick}
    >
      <div
        className="SelectionProviderBrowserPhoto-image"
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
      {isSelected && (
        <div className="SelectionProviderBrowserPhoto-used">
          <IconCheckFilled />
        </div>
      )}
      {showCount && count > 1 && <div className="SelectionProviderBrowserPhoto-count">{count}</div>}
    </div>
  );

  return (
    <>
      {draggable === false && renderedPhoto}
      {draggable === true && (
        <DraggableContainer
          dispatch={dispatch}
          selectionPhoto={selectionPhoto}
          uploadOrNot={uploadOrNot}
        >
          {renderedPhoto}
        </DraggableContainer>
      )}
    </>
  );
};

export default SelectionProviderBrowserPhoto;

const DraggableContainer: FunctionComponent<{
  children: ReactNode;
  dispatch: Dispatch<CustomizationAction>;
  selectionPhoto: SelectionPhoto;
  uploadOrNot: () => void;
}> = ({ children, dispatch, selectionPhoto, uploadOrNot }) => {
  const onDragStart = () => {
    uploadOrNot();

    dispatch({
      draggedPhoto: { from: "selection", selectionPhoto },
      type: "setDraggedPhoto",
    });
  };

  const onDragEnd = () => {
    dispatch({ type: "setDraggedPhoto", draggedPhoto: null });
  };

  return (
    <Draggable draggable={true} onDragEnd={onDragEnd} onDragStart={onDragStart}>
      {children}
    </Draggable>
  );
};
